<template>
  <span class="cf-input">
    <!-- boolean -->
    <ion-toggle v-if="fieldType === 'boolean'" :checked="!!modelValue" @ionChange="emit('update:modelValue', $event.detail.checked)" />

    <!-- datetime: the picker shows the wall clock of the configured timezone -->
    <template v-else-if="fieldType === 'datetime'">
      <ion-datetime-button :datetime="pickerId" />
      <ion-modal :keep-contents-mounted="true">
        <ion-datetime
          :id="pickerId"
          :presentation="config.format"
          :value="pickerValue"
          hour-cycle="h23"
          :first-day-of-week="1"
          @ionChange="onPicked($event.detail.value)"
        />
      </ion-modal>
    </template>

    <!-- currency: {amount (minor unit of the currency), currency} -->
    <span v-else-if="fieldType === 'currency'" class="cf-currency-row">
      <ion-input
        data-test="cf-currency-amount"
        type="text"
        inputmode="decimal"
        :value="amountText"
        :placeholder="amountText ? '' : amountToText(0, currencyCode)"
        style="min-width: 80px"
        @ionInput="onAmountInput"
        @keydown="onAmountKeydown"
      />
      <span v-if="currencyConfig.fixed" data-test="cf-currency-code" class="cf-currency-code">{{ currencyCode }}</span>
      <ion-select
        v-else
        data-test="cf-currency-select"
        aria-label="Currency"
        interface="popover"
        :value="currencyCode"
        :selected-text="currencyCode"
        @ionChange="onCurrencyChange(String($event.detail.value))"
      >
        <ion-select-option v-for="option in currencyOptions()" :key="option.code" :value="option.code">
          {{ option.code }} — {{ option.name }}
        </ion-select-option>
      </ion-select>
    </span>

    <!-- integer -->
    <ion-input
      v-else-if="fieldType === 'integer'"
      type="number"
      step="1"
      :value="modelValue ?? ''"
      @ionInput="emit('update:modelValue', parseInt(($event.target as HTMLInputElement).value, 10) || 0)"
    />

    <!-- decimal -->
    <ion-input
      v-else-if="fieldType === 'decimal'"
      type="number"
      :value="modelValue ?? ''"
      @ionInput="emit('update:modelValue', parseFloat(($event.target as HTMLInputElement).value) || 0)"
    />

    <!-- url -->
    <ion-input
      v-else-if="fieldType === 'url'"
      type="url"
      :value="modelValue ?? ''"
      placeholder="https://"
      @ionInput="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
    />

    <!-- email -->
    <ion-input
      v-else-if="fieldType === 'email'"
      type="email"
      :value="modelValue ?? ''"
      @ionInput="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
    />

    <!-- password, encrypted: only set again, the stored value is never shown or edited -->
    <span v-else-if="fieldType === 'password' && encryptedUnlock" class="cf-password-row">
      <ion-button fill="outline" size="small" @click.stop="setEncryptedPassword">
        <ion-icon slot="start" :icon="lockClosedOutline" />
        {{ unlockOf(modelValue) ? 'Change password' : 'Set password' }}
      </ion-button>
    </span>

    <!-- password -->
    <span v-else-if="fieldType === 'password'" class="cf-password-row">
      <ion-input
        :type="showPassword ? 'text' : 'password'"
        :value="plainPassword"
        @ionInput="onPasswordInput"
      />
      <ion-button fill="clear" size="small" @click.stop="showPassword = !showPassword">
        <ion-icon slot="icon-only" :icon="showPassword ? eyeOffOutline : eyeOutline" />
      </ion-button>
    </span>

    <!-- object_ref -->
    <ion-button v-else-if="fieldType === 'object_ref'" fill="outline" size="small" @click="pickObject">
      {{ selectedObjectName ?? 'Choose item…' }}
    </ion-button>

    <!-- image_ref -->
    <ion-button v-else-if="fieldType === 'image_ref'" fill="outline" size="small" @click="emit('pick-image')">
      {{ modelValue ? 'Image: ' + String(modelValue).slice(0, 8) + '…' : 'Choose image…' }}
    </ion-button>

    <!-- user_ref: simplified as text input until user list endpoint added -->
    <ion-input
      v-else-if="fieldType === 'user_ref'"
      type="number"
      step="1"
      :value="modelValue ?? ''"
      placeholder="User ID"
      @ionInput="emit('update:modelValue', parseInt(($event.target as HTMLInputElement).value, 10) || null)"
    />

    <!-- barcode_ref: text input for barcode code lookup -->
    <ion-input
      v-else-if="fieldType === 'barcode_ref'"
      :value="modelValue ?? ''"
      placeholder="Barcode value"
      @ionInput="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
    />

    <!-- textarea -->
    <ion-textarea
      v-else-if="fieldType === 'textarea'"
      :value="modelValue ?? ''"
      auto-grow
      @ionInput="emit('update:modelValue', ($event.target as HTMLIonTextareaElement).value)"
    />

    <!-- text / fallback -->
    <ion-input
      v-else
      :value="modelValue ?? ''"
      @ionInput="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
    />
  </span>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useId, watch } from 'vue';
import {
  IonButton, IonDatetime, IonDatetimeButton, IonIcon, IonInput, IonModal, IonSelect, IonSelectOption, IonTextarea, IonToggle,
} from '@ionic/vue';
import { eyeOutline, eyeOffOutline, lockClosedOutline } from 'ionicons/icons';
import { unlockOf, type UnlockMode } from '@/helper/passwordCrypto';
import { askAndEncryptPassword } from '@/composables/usePasswordUnlock';
import { useApiStore } from '@/stores/api';
import type { CustomFieldType, PasswordMode, TypeConfig } from '@/models/custom-fields';
import { fromPickerValue, nowFor, resolveDateTimeConfig, toPickerValue } from '@/helper/datetime';
import {
  amountToText, convertMinorUnits, currencyOptions, deleteBackward, formatWhileTyping, resolveCurrencyConfig, textToAmount, type TypedText,
} from '@/helper/currency';

const props = withDefaults(defineProps<{
  fieldType: CustomFieldType;
  passwordMode?: PasswordMode | null;
  typeConfig?: TypeConfig | null;
  /** Fill an empty `datetime` with now if its `default_now` says so. */
  prefillNow?: boolean;
  modelValue: any;
}>(), {
  prefillNow: true,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void;
  (e: 'pick-image'): void;
  (e: 'pick-object'): void;
}>();

const api = useApiStore();
const showPassword = ref(false);

const pickerId = `cf-datetime-${useId()}`;
const config = computed(() => resolveDateTimeConfig(props.typeConfig));
const pickerValue = computed(() => toPickerValue(props.modelValue, config.value));

function onPicked(picked: unknown) {
  emit('update:modelValue', fromPickerValue(picked, config.value));
}

onMounted(() => {
  const isDateTime = props.fieldType === 'datetime';
  const empty = props.modelValue === null || props.modelValue === undefined || props.modelValue === '';
  if (props.prefillNow && isDateTime && empty && config.value.defaultNow) {
    emit('update:modelValue', nowFor(config.value));
  }
});

const currencyConfig = computed(() => resolveCurrencyConfig(props.typeConfig));
const currencyCode = computed<string>(() => {
  const value = props.modelValue;
  if (currencyConfig.value.fixed || !value || typeof value !== 'object' || !value.currency) return currencyConfig.value.currency;
  return value.currency;
});

function amountOf(value: any): number | null {
  return value && typeof value === 'object' && typeof value.amount === 'number' ? value.amount : null;
}

// What is typed stays in `amountText`, it is not derived from the value on every key (that would add ",00" while typing).
const amountText = ref(amountOf(props.modelValue) === null ? '' : amountToText(amountOf(props.modelValue)!, currencyCode.value));
watch(() => props.modelValue, (value) => {
  if (amountOf(value) !== textToAmount(amountText.value, currencyCode.value)) {
    amountText.value = amountOf(value) === null ? '' : amountToText(amountOf(value)!, currencyCode.value);
  }
});

function emitAmount(text: string, code: string) {
  emit('update:modelValue', { amount: textToAmount(text, code) ?? 0, currency: code });
}

/** Shows the cleaned up text with the caret where it belongs, in the native input as well, which has not seen the change. */
function showTyped(ionInput: HTMLIonInputElement, native: HTMLInputElement, typed: TypedText) {
  native.value = typed.text;
  native.setSelectionRange(typed.caret, typed.caret);
  ionInput.value = typed.text;
  amountText.value = typed.text;
  emitAmount(typed.text, currencyCode.value);
}

async function onAmountInput(ev: Event) {
  const ionInput = ev.target as HTMLIonInputElement;
  const native = await ionInput.getInputElement();
  showTyped(ionInput, native, formatWhileTyping(native.value, currencyCode.value, native.selectionStart ?? native.value.length));
}

// Backspace behind a separator also removes the digit before it, the separators are then placed again.
function onAmountKeydown(ev: KeyboardEvent) {
  const native = ev.target as HTMLInputElement;
  if (ev.key !== 'Backspace' || native.selectionStart !== native.selectionEnd) return;
  const typed = deleteBackward(native.value, native.selectionStart ?? 0, currencyCode.value);
  if (!typed) return;
  ev.preventDefault();
  showTyped(native.closest('ion-input') as HTMLIonInputElement, native, typed);
}

function onCurrencyChange(code: string) {
  if (code === currencyCode.value) return;
  // The new currency may have other fraction digits and separators: keep the number, write it in the new format.
  const amount = textToAmount(amountText.value, currencyCode.value);
  const converted = amount === null ? '' : amountToText(convertMinorUnits(amount, currencyCode.value, code), code);
  amountText.value = converted;
  emitAmount(converted, code);
}

const plainPassword = computed(() => {
  if (!props.modelValue) return '';
  if (props.passwordMode && props.passwordMode !== 'plain') return '';
  return typeof props.modelValue === 'string' ? props.modelValue : '';
});

const encryptedUnlock = computed<UnlockMode | null>(() => {
  if (props.passwordMode === 'encrypted') return 'session';
  return props.passwordMode === 'always_encrypted' ? 'every_access' : null;
});

async function setEncryptedPassword() {
  if (!encryptedUnlock.value) return;
  const stored = await askAndEncryptPassword(encryptedUnlock.value);
  if (stored !== null) emit('update:modelValue', stored);
}

function onPasswordInput(ev: Event) {
  emit('update:modelValue', (ev.target as HTMLInputElement).value);
}

const selectedObjectName = computed<string | null>(() => {
  if (!props.modelValue) return null;
  return api.byId(Number(props.modelValue))?.data?.object?.name ?? null;
});

function pickObject() {
  emit('pick-object');
}
</script>

<style scoped>
.cf-input {
  display: inline-flex;
  align-items: center;
  width: 100%;
}
.cf-currency-row {
  display: inline-flex;
  gap: 4px;
  align-items: center;
  width: 100%;
}
.cf-currency-code {
  font-weight: 600;
  padding: 0 8px;
}
.cf-password-row {
  display: inline-flex;
  align-items: center;
  width: 100%;
  gap: 4px;
}
</style>
